import {
  addDays,
  differenceInCalendarDays,
  differenceInDays,
  isAfter,
  isSameDay,
  max,
  parse
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { Study } from '../models/Study';
import { StudyParticipant } from '../models/StudyParticipant';
import {
  filterOutRemovedOccurrences,
  getAllEventOccurrences,
  ParticipantEventOccurrence
} from '../screens/study/helpers/ParticipantEventHelpers';

export function calculateStudyEndDate(
  startDate: Date | number,
  studyLength: number
) {
  // copy date to prevent mutation
  let date = new Date(startDate);
  date.setHours(0, 0, 0);
  // set end date to 11:59:59 pm of the prior day to get exact end date
  studyLength = studyLength > 0 ? studyLength - 1 : 0;
  date = addDays(date, studyLength);
  date.setHours(23, 59, 59);
  return date;
}

/**
 * Keep this in sync with the chloe-api calculateStudyParticipantDates.ts.
 * studyParticipant needs to have event and event log data to properly check if study is completed
 * as it will be used to check if there are still any pending events.
 */
export function calculateStudyParticipantDates(
  study?: Study,
  studyParticipant?: StudyParticipant,
  shouldUseIntendedStartDate = false,
  timeZone?: string
) {
  let startDate = studyParticipant?.startDate
    ? studyParticipant.startDate
    : shouldUseIntendedStartDate && studyParticipant?.intendedStartDate
      ? parse(studyParticipant?.intendedStartDate, 'yyyy-MM-dd', new Date())
      : undefined;

  // make a copy of startDate to prevent mutation
  if (startDate)
    startDate = new Date(
      new Date(
        timeZone ? utcToZonedTime(startDate, timeZone) : startDate
      ).setHours(0, 0, 0)
    );
  // make a copy of endDateDate to prevent mutation
  let actualCompletionDate = studyParticipant?.withdrawnDate
    ? new Date(studyParticipant.withdrawnDate)
    : studyParticipant?.screenFailedDate
      ? new Date(studyParticipant.screenFailedDate)
      : studyParticipant?.actualEndDate
        ? new Date(studyParticipant.actualEndDate)
        : undefined;

  if (actualCompletionDate && timeZone) {
    actualCompletionDate = utcToZonedTime(actualCompletionDate, timeZone);
  }

  const expectedStudyLength = Math.max(
    studyParticipant?.studyLength ?? 0, // Calculated studyLength from all event occurrences
    study?.studyLength ?? 0
  );
  // if endDate is not specified than calculate from the studyLength. Sponsored
  // studies would specify specific endDate or withdrawnDate
  let endDate: Date | undefined;
  if (startDate) {
    if (actualCompletionDate) {
      // Use Actual Date when user withdrawn, screen failed, or completed.
      endDate = actualCompletionDate;
    } else {
      // Otherwise use projected end date logic
      const studyLengthBasedDate = calculateStudyEndDate(
        startDate,
        expectedStudyLength
      );
      const projectedDate = studyParticipant?.projectedEndDate
        ? timeZone
          ? utcToZonedTime(
              new Date(studyParticipant.projectedEndDate),
              timeZone
            )
          : new Date(studyParticipant.projectedEndDate)
        : null;

      endDate = projectedDate
        ? max([studyLengthBasedDate, projectedDate])
        : studyLengthBasedDate;
    }
  }
  let studyLength =
    startDate && actualCompletionDate
      ? differenceInDays(actualCompletionDate, startDate) + 1
      : expectedStudyLength;
  let actualEndDate =
    startDate && calculateStudyEndDate(startDate, expectedStudyLength);
  if (studyLength > 9999) studyLength = 9999;

  // Use the later date between projectedEndDate and studyLengthBasedEndDate
  let projectedEndDate =
    startDate && !actualCompletionDate
      ? (() => {
          const studyLengthBasedDate = calculateStudyEndDate(
            startDate,
            study?.studyLength ?? 0
          );
          const participantProjectedDate = studyParticipant?.projectedEndDate
            ? new Date(
                timeZone
                  ? utcToZonedTime(studyParticipant.projectedEndDate, timeZone)
                  : studyParticipant.projectedEndDate
              )
            : null;
          if (participantProjectedDate) {
            return max([studyLengthBasedDate, participantProjectedDate]);
          }
          return studyLengthBasedDate;
        })()
      : endDate;

  const projectedStudyLength =
    startDate && projectedEndDate
      ? differenceInCalendarDays(projectedEndDate, startDate) + 1
      : undefined;

  // Check if completed all activities in the study
  let eventOccurrences: ParticipantEventOccurrence[] = [];
  if (studyParticipant) {
    for (const participantEvent of studyParticipant?.participantEvents || []) {
      const { past, future } = getAllEventOccurrences(
        participantEvent,
        studyParticipant,
        { timeZone }
      );
      eventOccurrences.push(...past, ...future);
    }
  }

  // filter out non participant accessible events
  eventOccurrences = filterOutRemovedOccurrences(
    eventOccurrences.filter(e => e.participantEvent.participantAccessible)
  );

  const now = timeZone ? utcToZonedTime(new Date(), timeZone) : new Date();

  // fix for issue study not completing at the end date
  if (
    (projectedStudyLength &&
      study?.studyLength &&
      projectedStudyLength > study?.studyLength) ||
    !study?.studyLength
  ) {
    endDate = projectedEndDate;
  } else if (
    startDate &&
    studyParticipant?.studyLength &&
    study?.studyLength &&
    studyParticipant?.studyLength > study?.studyLength &&
    eventOccurrences.length === 0
  ) {
    // use study length based end date if all events are completed as long expiry forms can make the study go way over the end date even if all forms are completed
    endDate = calculateStudyEndDate(startDate, study?.studyLength);
  }

  const isCompletedStudy =
    studyParticipant?.actualEndDate ||
    studyParticipant?.withdrawnDate ||
    studyParticipant?.screenFailedDate ||
    studyParticipant?.disqualified ||
    study?.status === 'Completed' ||
    study?.status === 'Locked'
      ? true
      : endDate
        ? ((isAfter(now, endDate) || isSameDay(now, endDate)) &&
            eventOccurrences.length === 0) ||
          // By default studyParticipant?.studyLength is the calculated studyLength from event occurrences
          // If the calculated studyLength is greater than the length from studyTemplate or study
          // Mark the study as completed when all events are completed as stated here: https://peoplescience-health.atlassian.net/browse/MAIN-54?focusedCommentId=11407
          (isAfter(now, endDate) &&
            eventOccurrences.length === 0 &&
            studyParticipant?.studyLength === studyLength) ||
          (projectedEndDate &&
            isAfter(now, projectedEndDate) &&
            eventOccurrences.length === 0 &&
            studyParticipant?.studyLength === studyLength) ||
          false
        : false;

  const isProjectedComplete = isCompletedStudy
    ? isCompletedStudy
    : projectedEndDate
      ? (isAfter(now, projectedEndDate) || isSameDay(now, projectedEndDate)) &&
        eventOccurrences.length === 0
      : false;

  // convert dates back to UTC on return
  if (timeZone) {
    endDate = endDate && zonedTimeToUtc(endDate, timeZone);
    projectedEndDate =
      projectedEndDate && zonedTimeToUtc(projectedEndDate, timeZone);
    startDate = startDate && zonedTimeToUtc(startDate, timeZone);
    actualEndDate = actualEndDate && zonedTimeToUtc(actualEndDate, timeZone);
  }

  return {
    startDate,
    endDate,
    actualEndDate,
    studyLength,
    isCompletedStudy,
    projectedEndDate,
    projectedStudyLength,
    isProjectedComplete
  };
}
